import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Logo } from "../components/Logo";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate("/console");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f9f9f8] flex flex-col">
      <header className="h-16 border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 h-full flex items-center">
          <Link to="/" data-testid="login-logo-link"><Logo /></Link>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-sm fade-up">
          <h1 className="font-heading font-light text-3xl tracking-tight">Sign in to your console</h1>
          <p className="text-sm text-neutral-500 mt-2">Manage your teams, SOPs and quality scores.</p>
          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Email</label>
              <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full bg-white border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111] focus:border-transparent" placeholder="you@company.com" />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Password</label>
              <div className="relative mt-1.5">
                <input data-testid="login-password-input" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-neutral-300 rounded-md px-3.5 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#111] focus:border-transparent" placeholder="••••••••" />
                <button type="button" data-testid="login-password-toggle" onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700 transition-colors">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
            <button data-testid="login-submit-button" type="submit" disabled={loading}
              className="w-full bg-[#111] text-white rounded-full py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-60">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <p className="text-sm text-neutral-500 mt-8">
            No account yet? <Link to="/register" data-testid="login-to-register-link" className="text-[#111] underline underline-offset-4">Create your company console</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
